import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs';
import { formatarData, formatarPlaca } from 'src/app/core/utils/utilities';
import {
  IGetQuantidadeVeiculosPatioListItem,
  IGetQuantidadeVeiculosPatioListResponse
} from 'src/app/services/ReportService/report.model';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';
import { ReportService } from 'src/app/services/ReportService/report.service';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';

type TipoTelaRelatorio =
  | 'patio'
  | 'periodo'
  | 'agendados-periodo'
  | 'saida-periodo-janela'
  | 'transportadora';

@Component({
  selector: 'app-quantidade-veiculos-patio',
  templateUrl: './quantidade-veiculos-patio.component.html',
  styleUrls: ['./quantidade-veiculos-patio.component.scss']
})
export class QuantidadeVeiculosPatioComponent {
  reportType: TipoTelaRelatorio = 'patio';
  reportTitle: string = 'Relatório de Veículos';

  form = this.initForm();
  isLoading: boolean = false;
  numeroPagina: number = 0;
  maxPages: number = 1;
  tableData: Array<any> = [];

  pesquisaParams = {
    PAGE_NUMBER: 1,
    PAGE_SIZE: 8
  };

  readonly tipoDataOptions = [
    { value: 1, name: 'Data de Entrada' },
    { value: 2, name: 'Data de Agendamento' },
    { value: 3, name: 'Data de Saída' }
  ];

  readonly simNaoTodosOptions = [
    { value: null, name: 'Todos' },
    { value: 1, name: 'Sim' },
    { value: 0, name: 'Não' }
  ];

  readonly situacaoOptions = [
    { value: null, name: 'Todos' },
    { value: 1, name: 'Pátio' },
    { value: 2, name: 'Saiu' },
    { value: 3, name: 'Cancelado' }
  ];

  readonly periodoOptions = [
    { value: null, name: 'Todos' },
    { value: 1, name: 'Manhã' },
    { value: 2, name: 'Tarde' },
    { value: 3, name: 'Noite' }
  ];

  tableColumns = [
    { header: 'Cód. Gate Entrada', key: 'codigoEntrada', type: 'text' },
    { header: 'Data Entrada', key: 'dataEntrada', type: 'text' },
    { header: 'Data Agendamento', key: 'dataAgendamento', type: 'text' },
    { header: 'Data Saída', key: 'dataSaida', type: 'text' },
    { header: 'Janela Saída', key: 'janelaSaida', type: 'text' },
    { header: 'Transportadora', key: 'transportadora', type: 'text' },
    { header: 'Placa Cavalo', key: 'placaCavalo', type: 'text' },
    { header: 'Motorista', key: 'motorista', type: 'text' },
    { header: 'Cód. Agendamento', key: 'codigoAgendamento', type: 'text' },
    { header: 'Situação', key: 'situacao', type: 'text' },
    { header: 'Agendado', key: 'agendado', type: 'text' }
  ];

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private reportService: ReportService,
    private appExportBase64: AppExportBase64,
    private swalAlert: SwalAlertService
  ) {}

  private initForm() {
    return this.fb.group({
      PK_GATE_ENTR: [null],
      NM_EPSA: [null],
      NM_MTRT: [null],
      TX_PLCA_CAVA: [null],
      DT_GATE_ENTR_INIC: [null],
      DT_GATE_ENTR_FIM: [null],
      TIPO_DATA_REFERENCIA: [1 as number | null],
      FL_AGENDADO: [null as number | null],
      NU_SITUACAO: [null as number | null],
      NU_PERIODO: [null as number | null],
      FL_PRE_PTIO: [null as number | null]
    });
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegments => {
      const ultimoSegmento = urlSegments[urlSegments.length - 1]?.path;
      this.reportType = this.getReportTypeByPath(ultimoSegmento);
      this.applyPresetByRoute();
      this.loadData();
    });
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.pesquisaParams.PAGE_NUMBER = 1;
      this.numeroPagina = 0;
      this.loadData();
    });
  }

  mudarPagina(pagina: number) {
    this.pesquisaParams.PAGE_NUMBER = pagina + 1;
    this.numeroPagina = pagina;
    this.loadData();
  }

  private getReportTypeByPath(path?: string): TipoTelaRelatorio {
    switch (path) {
      case 'relatorio-veiculos':
        return 'patio';
      case 'quantidade-veiculos-periodo':
        return 'periodo';
      case 'quantidade-veiculos-agendados-periodo':
        return 'agendados-periodo';
      case 'quantidade-veiculos-saida-periodo-janela':
        return 'saida-periodo-janela';
      case 'quantidade-veiculos-transportadora':
        return 'transportadora';
      default:
        return 'patio';
    }
  }

  private configureReportMode(): void {
    if (this.reportType === 'patio') {
      this.reportTitle = 'Relatório de veículos';
      return;
    }

    if (this.reportType === 'periodo') {
      this.reportTitle = 'Quantidade de Veículos por Período';
      return;
    }

    if (this.reportType === 'agendados-periodo') {
      this.reportTitle = 'Quantidade de Veículos Agendados por Período';
      return;
    }

    if (this.reportType === 'saida-periodo-janela') {
      this.reportTitle = 'Quantidade de Veículos que Saíram por Período/Janela';
      return;
    }

    this.reportTitle = 'Quantidade de Veículos por Transportadora';
  }

  private loadData(): void {
    this.getVeiculos();
  }

  private createPayload() {
    const pkGateEntr = this.form.controls.PK_GATE_ENTR.value;
    const placa = this.form.controls.TX_PLCA_CAVA.value;
    return {
      PK_GATE_ENTR:
        pkGateEntr != null && String(pkGateEntr).trim() !== ''
          ? String(pkGateEntr).trim()
          : null,
      NM_EPSA: this.form.controls.NM_EPSA.value ?? null,
      NM_MTRT: this.form.controls.NM_MTRT.value ?? null,
      TX_PLCA_CAVA:
        placa != null && String(placa).trim() !== ''
          ? String(placa).replace(/-/g, '').trim()
          : null,
      DT_GATE_ENTR_INIC: this.form.controls.DT_GATE_ENTR_INIC.value ?? null,
      DT_GATE_ENTR_FIM: this.form.controls.DT_GATE_ENTR_FIM.value ?? null,
      TIPO_DATA_REFERENCIA: this.form.controls.TIPO_DATA_REFERENCIA.value ?? null,
      FL_AGENDADO: this.form.controls.FL_AGENDADO.value ?? null,
      NU_SITUACAO: this.form.controls.NU_SITUACAO.value ?? null,
      NU_PERIODO: this.form.controls.NU_PERIODO.value ?? null,
      FL_PRE_PTIO: this.form.controls.FL_PRE_PTIO.value ?? null,
      PAGE_NUMBER: this.pesquisaParams.PAGE_NUMBER,
      PAGE_SIZE: this.pesquisaParams.PAGE_SIZE
    };
  }

  private applyPresetByRoute(): void {
    this.configureReportMode();

    const preset: {
      PK_GATE_ENTR: null;
      NM_EPSA: null;
      NM_MTRT: null;
      TX_PLCA_CAVA: null;
      DT_GATE_ENTR_INIC: null;
      DT_GATE_ENTR_FIM: null;
      TIPO_DATA_REFERENCIA: number;
      FL_AGENDADO: number | null;
      NU_SITUACAO: number | null;
      NU_PERIODO: number | null;
      FL_PRE_PTIO: number | null;
    } = {
      PK_GATE_ENTR: null,
      NM_EPSA: null,
      NM_MTRT: null,
      TX_PLCA_CAVA: null,
      DT_GATE_ENTR_INIC: null,
      DT_GATE_ENTR_FIM: null,
      TIPO_DATA_REFERENCIA: 1,
      FL_AGENDADO: null,
      NU_SITUACAO: null,
      NU_PERIODO: null,
      FL_PRE_PTIO: null
    };

    if (this.reportType === 'agendados-periodo') {
      preset.TIPO_DATA_REFERENCIA = 2;
      preset.FL_AGENDADO = 1;
    }

    if (this.reportType === 'saida-periodo-janela') {
      preset.TIPO_DATA_REFERENCIA = 3;
      preset.NU_SITUACAO = 2;
    }

    this.form.reset(preset, { emitEvent: false });
    this.pesquisaParams.PAGE_NUMBER = 1;
    this.numeroPagina = 0;
  }

  private formatFlag(value: number | null | undefined): string {
    return value === 1 ? 'Sim' : 'Não';
  }

  getVeiculos(): void {
    this.isLoading = true;
    const payload = this.createPayload();

    this.reportService.GetQuantidadeVeiculosPatioList(payload).subscribe({
      next: (response: IGetQuantidadeVeiculosPatioListResponse) => {
        this.tableData = response.ITEMS.map((item: IGetQuantidadeVeiculosPatioListItem) => {
          return {
            codigoEntrada: item.PK_GATE_ENTR ?? '---',
            dataEntrada: formatarData(item.DT_GATE_ENTR) ?? '---',
            dataAgendamento: formatarData(item.DT_AGND ?? '') ?? '---',
            dataSaida: formatarData(item.DT_GATE_SAID ?? '') ?? '---',
            janelaSaida: item.DS_JANELA_SAIDA ?? '---',
            transportadora: item.NM_TRANSPORTADORA ?? '---',
            placaCavalo: formatarPlaca(item.TX_PLCA_CAVA) ?? '---',
            motorista: item.NM_MTRT ?? '---',
            codigoAgendamento: item.PK_AGND ?? '---',
            situacao: item.DS_STATUS ?? '---',
            agendado: this.formatFlag(item.FL_AGENDADO)
          };
        });

        this.maxPages = response.TOTAL_PAGES;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  baixarQuantidadeVeiculosPatio(): void {
    this.isLoading = true;

    this.reportService
      .GetQuantidadeVeiculosPatio({
        PK_GATE_ENTR: this.form.controls.PK_GATE_ENTR.value,
        NM_EPSA: this.form.controls.NM_EPSA.value,
        NM_MTRT: this.form.controls.NM_MTRT.value,
        TX_PLCA_CAVA: this.form.controls.TX_PLCA_CAVA.value,
        DT_GATE_ENTR_INIC: this.form.controls.DT_GATE_ENTR_INIC.value,
        DT_GATE_ENTR_FIM: this.form.controls.DT_GATE_ENTR_FIM.value,
        TIPO_DATA_REFERENCIA: this.form.controls.TIPO_DATA_REFERENCIA.value,
        FL_AGENDADO: this.form.controls.FL_AGENDADO.value,
        NU_SITUACAO: this.form.controls.NU_SITUACAO.value,
        NU_PERIODO: this.form.controls.NU_PERIODO.value,
        FL_PRE_PTIO: this.form.controls.FL_PRE_PTIO.value
      })
      .subscribe({
      next: response => {
        this.appExportBase64.downloadExcel(response.ReportBase64, 'relatorio-veiculos');
      },
      error: (err) => {
        this.swalAlert.handleError(err, 'Atenção!', 'Não foi possível gerar o relatório de veículos.', 'warning');
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  baixarQuantidadeVeiculosPatioPdf(): void {
    this.isLoading = true;

    this.reportService
      .GetQuantidadeVeiculosPatioPdf({
        PK_GATE_ENTR: this.form.controls.PK_GATE_ENTR.value,
        NM_EPSA: this.form.controls.NM_EPSA.value,
        NM_MTRT: this.form.controls.NM_MTRT.value,
        TX_PLCA_CAVA: this.form.controls.TX_PLCA_CAVA.value,
        DT_GATE_ENTR_INIC: this.form.controls.DT_GATE_ENTR_INIC.value,
        DT_GATE_ENTR_FIM: this.form.controls.DT_GATE_ENTR_FIM.value,
        TIPO_DATA_REFERENCIA: this.form.controls.TIPO_DATA_REFERENCIA.value,
        FL_AGENDADO: this.form.controls.FL_AGENDADO.value,
        NU_SITUACAO: this.form.controls.NU_SITUACAO.value,
        NU_PERIODO: this.form.controls.NU_PERIODO.value,
        FL_PRE_PTIO: this.form.controls.FL_PRE_PTIO.value
      })
      .subscribe({
        next: response => {
          this.appExportBase64.downloadPDF(response.ReportBase64, 'Relatório Veículos.pdf');
        },
        error: (err) => {
          this.swalAlert.handleError(err, 'Atenção!', 'Não foi possível gerar o PDF de veículos.', 'warning');
        },
        complete: () => {
          this.isLoading = false;
        }
      });
  }
}
